import React from "react";
import { StarFilled } from "@ant-design/icons";
import { useDpatCommentFeedback } from "context/DpatCommentFeedbackContext";
import { useDpatIndicatorSlug } from "context/DpatIndicatorSlugContext";
import {
  findDetailScoreRowForSlug,
  indicatorKindFromSlug,
  isDpatCompletedOrClosedStatus,
  resolveRoleStandingNumericScores,
} from "utils/dpatManualScoreHelpers";

function slugFromScoreBannerLabel(label) {
  const text = String(label || "").trim();
  const match = text.match(/^(CI|SDI|PI)\s*(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)(i{1,3})?/i);
  if (!match) return { slug: null, part: null };
  const kind = match[1].toUpperCase();
  const prefix = kind === "CI" ? "c" : kind.toLowerCase();
  return {
    slug: `${prefix}${match[2]}-${match[3]}`,
    part: match[4] ? match[4].toLowerCase() : null,
  };
}

function finalScoreLabel(slug, label) {
  const kind = indicatorKindFromSlug(slug);
  const range = String(slug || "");
  const dash = range.lastIndexOf("-");
  if (kind && dash !== -1) {
    const left = kind === "CI" ? range.slice(1) : range.slice(kind.length);
    return `${kind} ${left} Final Score`;
  }
  const fromLabel = String(label || "").replace(/\s*(i{1,3})?\s*Actual Score:?/i, " Final Score").trim();
  return fromLabel || "Final Score";
}

export default function ActualScoreDisplay({ label, score, children, slug, hideWhenFinal = false }) {
  const { assessmentWorkflowStatus, scoreSheetSummary, assessmentStartTables } = useDpatCommentFeedback();
  const contextSlug = useDpatIndicatorSlug();
  const parsed = slugFromScoreBannerLabel(label);
  const resolvedSlug = slug || contextSlug || parsed.slug;
  const systemValue = score !== undefined && score !== null ? score : children;
  const showFinal = isDpatCompletedOrClosedStatus(assessmentWorkflowStatus);

  if (showFinal) {
    const genericLabel = String(label || "").trim().toLowerCase() === "actual score";
    if (hideWhenFinal || genericLabel || parsed.part === "ii" || parsed.part === "iii") {
      return null;
    }

    const row = findDetailScoreRowForSlug(scoreSheetSummary, resolvedSlug, assessmentStartTables);
    const standing = row ? resolveRoleStandingNumericScores(row) : null;
    const displayValue =
      standing?.finalScore != null && standing.finalScore !== ""
        ? standing.finalScore
        : systemValue;
    const displayLabel = finalScoreLabel(resolvedSlug, label);

    return (
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "8px 12px",
          borderRadius: 8,
          border: "1.5px solid #d48806",
          background: "linear-gradient(135deg, #fffbe6 0%, #ffe58f 100%)",
          boxShadow: "0 2px 8px rgba(212, 136, 6, 0.15)",
        }}
      >
        <StarFilled style={{ fontSize: 13, color: "#d48806" }} />
        <span style={{ fontSize: 11, fontWeight: 600, color: "#614700" }}>{displayLabel}</span>
        <strong style={{ fontSize: 15, color: "#ad6800", lineHeight: 1, minWidth: 16, textAlign: "center" }}>
          {displayValue}
        </strong>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "8px 12px",
        borderRadius: 8,
        border: "1.5px solid #d48806",
        background: "linear-gradient(135deg, #fffbe6 0%, #ffe58f 100%)",
        boxShadow: "0 2px 8px rgba(212, 136, 6, 0.15)",
      }}
    >
      <StarFilled style={{ fontSize: 13, color: "#d48806" }} />
      <span style={{ fontSize: 11, fontWeight: 600, color: "#614700" }}>{label}</span>
      <strong style={{ fontSize: 15, color: "#ad6800", lineHeight: 1, minWidth: 16, textAlign: "center" }}>
        {systemValue}
      </strong>
    </div>
  );
}
